const { getElement, getElements, getOffsetTop } = window.BAO.Utils

// eslint-disable-next-line no-undef
class LoadMoreList extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.currentPage =
      this.options.currentPage < 2 ? 1 : this.options.currentPage

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.setCurrentAmountViewed()

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    if (window.location.hash) {
      // Scroll to correct position on 'back'. Need to wait for complete due to Chrome returning to previous position
      document.addEventListener('readystatechange', () => {
        if (document.readyState !== 'complete') {
          return
        }

        window.setTimeout(() => {
          const previousItem = getElement(
            `[data-id="${window.location.hash.replace('#', '')}"]`,
          )

          const offsetTop = getOffsetTop(previousItem)
          const lowerBound = window.scrollY - 200
          const upperBound = window.scrollY + 200

          if (!(lowerBound <= offsetTop && offsetTop <= upperBound)) {
            this.scrollToElement(previousItem)
          }

          // this.setupObservers()
          window.history.pushState({}, '', window.location.href.split('#')[0])
        }, 1)
      })
    }
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.trigger && this.els.trigger.exists) {
      this.listeners.add(
        this.els.trigger.element,
        'click',
        this.handleClick.bind(this),
      )
    }

    if (this.els.previous && this.els.previous.exists) {
      this.listeners.add(
        this.els.previous.element,
        'click',
        this.handleClickPrevious.bind(this),
      )
    }

    if (this.dataset.infiniteScroll === 'true') {
      this.onScroll = this.onScroll.bind(this)
      window.addEventListener('scroll', this.onScroll, { passive: true })
    }

    if (this.currentPage !== 1 && this.currentPage > 1) {
      window.addEventListener('load', this.showPreviousButton.bind(this))
    }

    if (this.currentPage >= this.options.totalPages) {
      this.currentPage = this.options.totalPages
      window.addEventListener('load', this.hideLoadMore.bind(this))
    }

    this.els.item.elements.forEach(itemEl => {
      this.listeners.add(itemEl, 'click', this.updateHash)
      this.listeners.add(itemEl, 'onKeydown', this.updateHash)
    })
  }

  handleClick () {
    this.loadMore()
  }

  handleClickPrevious () {
    this.loadPrevious()
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.checkTriggerStatus()
  }

  showPreviousButton () {
    this.els.previous.element.setAttribute('aria-hidden', false)
  }

  hidePreviousButton () {
    this.els.previous.element.setAttribute('aria-hidden', true)
  }

  hideLoadMore () {
    if (!this.els.pagination?.element) return
    this.els.pagination?.element.setAttribute('aria-hidden', true)
  }

  async loadMore () {
    this.loading = true

    // eslint-disable-next-line compat/compat
    const queries = new URLSearchParams(window.location.search)
    queries.set('page', (this.currentPage += 1).toString())

    window.history.pushState({}, '', `?${queries.toString()}`)

    if (this.options.sectionId && this.options.sectionId.length) {
      queries.set('sections', this.options.sectionId)
    }

    const sectionHTML = await this.fetchSection(queries.toString())
    const newSectionDOM = new DOMParser().parseFromString(
      sectionHTML,
      'text/html',
    )

    this.dataset.currentPage = this.currentPage

    this.appendNewItemsToTarget(newSectionDOM)

    if (this.currentPage >= this.options.totalPages) {
      this.hideLoadMore()
    }

    this.loading = false
  }

  async loadPrevious () {
    if (this.currentPage <= 1) {
      this.currentPage = 1
      this.dataset.currentPage = this.currentPage
      this.hidePreviousButton()

      return
    }

    this.loading = true

    // eslint-disable-next-line compat/compat
    const queries = new URLSearchParams(window.location.search)

    let previousPage = this.currentPage - 1

    if (this.currentPage > this.options.firstPage) {
      previousPage = this.options.firstPage - 1
    }

    this.currentPage = previousPage
    this.dataset.currentPage = Number(previousPage)

    queries.set('page', previousPage.toString())

    window.history.pushState({}, '', `?${queries.toString()}`)

    if (this.options.sectionId && this.options.sectionId.length) {
      queries.set('sections', this.options.sectionId)
    }

    const sectionHTML = await this.fetchSection(queries.toString())
    const newSectionDOM = new DOMParser().parseFromString(
      sectionHTML,
      'text/html',
    )

    if (this.dataset.currentPage <= 1) {
      this.hidePreviousButton()
    }

    this.prependNewItemsToTarget(newSectionDOM)

    this.setCurrentAmountViewed()

    this.loading = false
  }

  checkTriggerStatus () {
    if (this.els.trigger.element.getAttribute('aria-hidden') === 'true') return

    const elementTriggerPoint =
      getOffsetTop(this.els.trigger.element) -
      window.innerHeight -
      window.innerHeight / 2

    if (this.lastKnownY < elementTriggerPoint || this.loading === true) return

    if (this.currentPage >= this.options.totalPages) {
      this.currentPage = this.options.totalPages
      this.dataset.currentPage = this.currentPage

      this.hideLoadMore()

      return
    }

    this.loadMore()
  }

  fetchSection (queryString) {
    if (this.options.sectionId && this.options.sectionId.length) {
      return fetch(`${this.options.url}/?${queryString}`)
        .then(response => response.json())
        .then(response => response[this.options.sectionId])
    } else {
      return fetch(`${this.options.url}/?${queryString}`)
        .then(response => response.text())
        .then(response => {
          return response
        })
    }
  }

  appendNewItemsToTarget (DOM) {
    const fragment = document.createDocumentFragment()
    const newItems = getElements(this.options.itemSelector, { context: DOM })
    newItems.forEach(item => fragment.appendChild(item))

    this.els.target.element.appendChild(fragment)
    this.setCurrentAmountViewed()

    if (this.currentItemCount >= this.options.totalItems) {
      this.els.trigger.element.remove()

      window.removeEventListener('scroll', this.onScroll, { passive: true })
    }

    newItems.forEach(itemEl => {
      this.listeners.add(itemEl, 'click', this.updateHash)
    })
  }

  prependNewItemsToTarget (DOM) {
    const fragment = document.createDocumentFragment()
    const newItems = getElements(this.options.itemSelector, { context: DOM })
    newItems.forEach(item => fragment.append(item))

    this.els.target.element.prepend(fragment)

    this.setCurrentAmountViewed()

    if (this.currentItemCount >= this.options.totalItems) {
      this.els.trigger.element.remove()

      window.removeEventListener('scroll', this.onScroll, { passive: true })
    }

    newItems.forEach(itemEl => {
      this.listeners.add(itemEl, 'click', this.updateHash)
    })
  }

  setCurrentAmountViewed () {
    if (this.els.text) {
      const currentCount =
        this.currentItemCount < this.options.totalItems
          ? this.currentItemCount
          : this.options.totalItems
      const buttonText = this.els.text.element.dataset.translation
      this.els.text.element.innerText = buttonText
        ? buttonText.replace('[count]', currentCount)
        : this.els.text.element.innerText
    }

    // const progress = (this.currentItemCount / this.options.totalItems) * 100
    // this.els.bar.element.style.setProperty(
    //   '--Transform_Progress',
    //   `${progress}%`,
    // )

    if (!this.els.current) return
    this.els.current.element.textContent = this.currentItemCount
  }

  /** @param {Event} e */
  updateHash (e) {
    if (!e.target.hasAttribute('href')) return

    if (!this.dataset.type === 'blog') {
      e.preventDefault()
    }
    const listItem = e.target.closest('.prd-List_Item')
    if (!listItem) return

    const id = listItem.dataset.id
    const page = listItem.dataset.page

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location)
    url.searchParams.set('page', page)
    url.hash = id

    window.history.pushState({}, '', `${url.search}${url.hash}`)
    window.location = e.target.getAttribute('href')
  }

  /** @param {HTMLElement} el */
  scrollToElement (el) {
    if (!el) {
      return
    }

    /**
     * @note For some reason el.scrollIntoView() wasn't working here,
     * so we're having to calculate the top of the element and scroll the
     * window to that position manually
     */

    // const headerHeight = parseInt(
    //   window.getComputedStyle(this.el).getPropertyValue(HEADER_HEIGHT_CUSTOM_PROPERTY),
    //   10,
    // )

    const boundingRect = el.getBoundingClientRect()
    const distanceFromTopOfPage = window.pageYOffset + boundingRect.top

    window.scrollTo(0, distanceFromTopOfPage)
    // this.needsToJumpToCard = false
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())
  }

  get currentItems () {
    return getElements(this.options.itemSelector, {
      context: this,
    })
  }

  get currentItemCount () {
    return this.currentItems.length
  }

  get options () {
    return {
      itemSelector: this.dataset.itemSelector
        ? this.dataset.itemSelector
        : `[data-load-more-list-el="item"]`,
      perPage: parseInt(
        this.dataset.itemsPerPage ? this.dataset.itemsPerPage : 10,
        10,
      ),
      columnCount: this.dataset.columnCount
        ? parseInt(this.dataset.columnCount, 10)
        : 1,
      currentPage: parseInt(
        this.dataset.currentPage ? this.dataset.currentPage : 1,
        10,
      ),
      firstPage: parseInt(
        this.dataset.firstPage ? this.dataset.firstPage : 1,
        10,
      ),
      totalPages: parseInt(
        this.dataset.totalPages ? this.dataset.totalPages : 1,
        10,
      ),
      totalItems: parseInt(
        this.dataset.totalItems ? this.dataset.totalItems : 1,
        10,
      ),
      sectionId: this.dataset.sectionId ? this.dataset.sectionId : '',
      url: this.dataset.url ? this.dataset.url : '',
    }
  }
}

if (!customElements.get('load-more-list')) {
  customElements.define('load-more-list', LoadMoreList)
}
